export type PlanId = 'S' | 'A' | 'B' | 'C' | 'D'

export type BranchOption = {
  label: string
  subtitle?: string
  longSubtitle?: string
  primary?: boolean
  targetId: string | null
  targetAnchor?: string
  tooltip?: string
}

export type BranchPoint = {
  date: string
  options: BranchOption[]
  line: number
  timelineFlipVertical?: boolean
}

export type BranchNodeSection = {
  id: string
  title: string
  markdownFile?: string
  epilogueTitle?: string
}

export type BranchNode = {
  id: string
  anchor?: string
  title: string
  subtitle?: string
  longSubtitle?: string
  markdownFile?: string
  footnotesFile?: string
  content?: { markdown: string }
  summary?: string
  branchPoints?: BranchPoint[]
  ending?: string
  primary?: boolean
  planId?: PlanId
  sections?: BranchNodeSection[]
  floating?: boolean
  floatingHeight?: number
  expandedPlanLayout?: boolean
}

export type EffectiveComputeData = {
  dates: string[]
  usEffectiveCompute: number[]
  chinaEffectiveCompute: number[]
}

export type UnifiedBranchesData = {
  version: number
  rootId: string
  nodes: Record<string, BranchNode>
  effectiveComputeByBranch?: Record<string, EffectiveComputeData>
}

export type ResolvedBranchNodeSection = BranchNodeSection & {
  content: { markdown: string }
}

export type ResolvedBranchNode = BranchNode & {
  content: { markdown: string }
  sections?: ResolvedBranchNodeSection[]
  footnotesMarkdown?: string
}

export type ResolvedBranchesData = {
  version: number
  rootId: string
  nodes: Record<string, ResolvedBranchNode>
  effectiveComputeByBranch?: Record<string, EffectiveComputeData>
}

export const planLabels: {
  id: PlanId
  label: string
  subtitle: string
  tooltip: string
}[] = [
  {
    id: 'A',
    label: 'PLAN A',
    subtitle: 'The Deal',
    tooltip:
      'Offer China and other nations a deal to mutually verify and control AI development.',
  },
  {
    id: 'B',
    label: 'PLAN B',
    subtitle: 'Sabotage',
    tooltip:
      'Take aggressive action to slow down China, buying time to slow down for a few years rather than months.',
  },
  {
    id: 'C',
    label: 'PLAN C',
    subtitle: 'The Slowdown',
    tooltip:
      'Slow down for a few months during the intelligence explosion, to make some progress on alignment research.',
  },
  {
    id: 'D',
    label: 'PLAN D',
    subtitle: 'The Race',
    tooltip:
      'Race approximately as hard as you can to beat China and other companies to superintelligence.',
  },
]

export const PLAN_NAMES: Record<PlanId, string> = {
  S: 'Plan S',
  A: 'Plan A',
  B: 'Plan B',
  C: 'Plan C',
  D: 'Plan D',
}

export const PLAN_NODE_IDS: Record<PlanId, string> = {
  S: 'plan-s-root',
  A: 'plan-a-root',
  B: 'plan-b-root',
  C: 'plan-c-root',
  D: 'plan-d-root',
}

export const PLAN_SELECTOR_ROOT_ID = 'plan-selector'

export const PLAN_SELECTOR_ELEMENT_ID = 'plan-selector-buttons'

export const scrollPlanSelectorIntoView = (selectorElement?: HTMLDivElement | null) => {
  const element = selectorElement ?? document.getElementById(PLAN_SELECTOR_ELEMENT_ID)
  if (!element) return
  const selectorTop = element.getBoundingClientRect().top + window.scrollY
  const targetScrollTop = Math.max(0, selectorTop - window.innerHeight * 0.12)
  window.scrollTo({ top: targetScrollTop, behavior: 'smooth' })
}

export function getSelectedPlanFromPath(
  selectedPath: string[]
): PlanId | null {
  const planNodeIdToLetter = Object.fromEntries(
    Object.entries(PLAN_NODE_IDS).map(([k, v]) => [v, k as PlanId])
  )
  for (const nodeId of selectedPath) {
    if (planNodeIdToLetter[nodeId]) return planNodeIdToLetter[nodeId]
  }
  return null
}
